import React, { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { DataBoxesContainer } from 'src/components/atoms/DataBoxesContainer';
import {
  BrandTrackingConfig,
  useSosLocationDataQuery,
} from 'src/api/brandTracking';
import {
  getKeywords,
  toShareSeries,
  toVolumeSeries,
  filterByTimeRange,
  formatLatestDataMonth,
  TimeRangeMonths,
} from '../../../utils/shareOfSearch';
import { ShareOfSearchBox } from '../ShareOfSearchBox';
import { SearchVolumeBox } from '../SearchVolumeBox';

type BrandTrackingDashboardProps = {
  customerId: string;
  config: BrandTrackingConfig;
  selectedLocationId: number | null;
  timeRange: TimeRangeMonths;
};

export const BrandTrackingDashboard: React.FC<BrandTrackingDashboardProps> = ({
  customerId,
  config,
  selectedLocationId,
  timeRange,
}) => {
  const {
    data: datapoints = [],
    isLoading,
    isError,
  } = useSosLocationDataQuery(customerId, selectedLocationId);

  const keywords = useMemo(
    () => getKeywords(datapoints, config.brandKeyword),
    [datapoints, config.brandKeyword],
  );

  const shareData = useMemo(
    () => filterByTimeRange(toShareSeries(datapoints, keywords), timeRange),
    [datapoints, keywords, timeRange],
  );

  const volumeData = useMemo(
    () => filterByTimeRange(toVolumeSeries(datapoints, keywords), timeRange),
    [datapoints, keywords, timeRange],
  );

  const latestMonth = formatLatestDataMonth(config.latestDataDate);

  return (
    <Flex direction="column" gap={4}>
      {latestMonth && (
        <Text fontSize="sm" color="content.muted">
          Data through {latestMonth} — Search data lags up to ~2 months.
        </Text>
      )}

      <DataBoxesContainer>
        <ShareOfSearchBox
          title="Share of search"
          colSpan={12}
          minH="400px"
          data={shareData}
          keywords={keywords}
          brandKeyword={config.brandKeyword}
          isLoading={isLoading}
          isError={isError}
        />
        <SearchVolumeBox
          title="Search volume"
          colSpan={12}
          minH="400px"
          data={volumeData}
          keywords={keywords}
          brandKeyword={config.brandKeyword}
          isLoading={isLoading}
          isError={isError}
        />
      </DataBoxesContainer>
    </Flex>
  );
};
